import { useState, useEffect, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { useClickAway } from 'react-use';
import { noop } from 'utils/noop';
import { InputText } from 'ui/inputs/inputText/InputText';
import { Search } from 'ui/icons/action/Search';
import { ChevronDown } from 'ui/icons/navigation/ChevronDown';
import { useClickOn } from 'utils/hooks/useClickOn';
import { MenuItem } from './MenuItem';
import { getNewSelection } from './utils';
import {
  ChevronUp,
  MenuContainer,
  MenuInputTextWrapper,
  MenuItemWrapper,
  MenuListContainer,
  MenuListContainerWrapper,
  StyledIconWrapper,
  TriggerButton,
} from './Styled';
import { ALIGNMENT } from './constants';
import { MenuItemOption, MenuProps } from './types';

export const BaseMenu = ({
  buttonText = '',
  children,
  defaultSelectedOptionIds = [],
  filterInputPlaceholder = 'Search',
  handleSelectionChange = noop,
  handleStringQueryChange = noop,
  hasFilter = false,
  horizontalAlignment = ALIGNMENT.LEFT,
  id,
  isChild = false,
  isInputMenu = false,
  isDisabled = false,
  isOpen = false,
  isSelectMenu = false,
  options,
  stringQuery = '',
  verticalAlignment = ALIGNMENT.DOWN,
  closeOnClickOutside = true,
  ...props
}: MenuProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(isOpen);
  const [selectedOptions, setSelectedOptions] = useState(defaultSelectedOptionIds);
  const [stringQueryInput, setStringQueryInput] = useState<string | undefined>(stringQuery);
  const [filterText, setFilterText] = useState('');

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  useClickAway(wrapperRef, () => {
    if (closeOnClickOutside) {
      setIsMenuOpen(false);
    }
  });

  useClickOn(triggerRef, () => {
    if (!isDisabled) {
      setIsMenuOpen(!isMenuOpen);
    }
  });

  useEffect(() => {
    setIsMenuOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    setStringQueryInput(stringQuery);
  }, [stringQuery]);

  useEffect(() => {
    if (!isEqual(defaultSelectedOptionIds, selectedOptions)) {
      setSelectedOptions(defaultSelectedOptionIds);
    }
  }, [defaultSelectedOptionIds]);

  const onSelectionChange = (selectedOption: string) => {
    if (isSelectMenu) {
      const newSelection = getNewSelection(selectedOption, selectedOptions);
      setSelectedOptions(newSelection);
      handleSelectionChange(selectedOption, newSelection);
    } else {
      setSelectedOptions([selectedOption]);
      handleSelectionChange(selectedOption);
    }
  };

  const onStringQueryChange = (string?: string) => {
    setStringQueryInput(string);
    handleStringQueryChange(string);
  };

  const filteredOptions = hasFilter
    ? options.filter((opt) => opt.label?.toLowerCase().includes(filterText.toLowerCase()))
    : options;

  const getSelectedOptionIds = (menuItemOption: MenuItemOption) =>
    selectedOptions.filter((optionId) =>
      menuItemOption.options
        ?.flatMap((o) =>
          'isExpandableGroup' in o && o.isExpandableGroup
            ? o.options.map((oo) => oo.id).concat(o.id)
            : o.id,
        )
        .includes(optionId),
    );

  const menu = (
    <MenuListContainerWrapper
      data-test={`menu-${id}`}
      ref={wrapperRef}
      isChild={isChild}
      horizontalAlignment={horizontalAlignment}
      verticalAlignment={verticalAlignment}
      hasFilter={hasFilter}
      {...props}
    >
      <MenuListContainer data-test="menu-list-container">
        {hasFilter && (
          <MenuInputTextWrapper data-test="menu-input-text-wrapper">
            <InputText
              onChange={setFilterText}
              value={filterText}
              placeholder={filterInputPlaceholder}
              Prefix={<Search />}
            />
          </MenuInputTextWrapper>
        )}
        {filteredOptions.map((optionsPerItem, i) => {
          return (
            <MenuItemWrapper data-test="menu-item-wrapper" key={i}>
              <MenuItem
                data-test="menu-item"
                isSelectMenu={isSelectMenu}
                isCheckable={isSelectMenu}
                isSelected={selectedOptions.includes(optionsPerItem.id)}
                onSelectionChange={onSelectionChange}
                onStringQueryChange={onStringQueryChange}
                isInput={isInputMenu}
                stringQuery={stringQueryInput}
                selectedOptionIds={getSelectedOptionIds(optionsPerItem)}
                {...optionsPerItem}
              />
              {optionsPerItem.suffix}
            </MenuItemWrapper>
          );
        })}
      </MenuListContainer>
    </MenuListContainerWrapper>
  );

  if (isChild) {
    return (isMenuOpen && menu) || null;
  }

  return (
    <MenuContainer data-test="menu-container" {...props}>
      <span ref={triggerRef} data-test={`menu-trigger-button-${id}`}>
        {children || (
          <TriggerButton>
            {buttonText}
            <StyledIconWrapper data-test="menu-chevron">
              {verticalAlignment === ALIGNMENT.DOWN ? (
                <ChevronDown height={16} width={16} />
              ) : (
                <ChevronUp height={16} width={16} />
              )}
            </StyledIconWrapper>
          </TriggerButton>
        )}
      </span>
      {isMenuOpen && menu}
    </MenuContainer>
  );
};
